import { useState } from 'react';
import { AlertTriangle, Copy } from 'lucide-react';

/**
 * Single-use card that shows a freshly-generated KEK to the admin once and
 * lets them copy it. Used by `BackupSettingsModal` (backup-identity enrolment).
 * Parameterised over env-var name / file path / systemd unit so a future
 * KEK-in-env consumer can reuse it without forking.
 */
export interface KekOnceCardProps {
  kek: string;
  /** e.g. "DV_BACKUP_KEK". */
  envVarName: string;
  /** Path of the env file the KEK belongs in. */
  envFilePath: string;
  /** Service to restart after pasting, e.g. "dragonhq". */
  systemdUnit: string;
  onDismiss: () => void;
}

export function KekOnceCard({
  kek,
  envVarName,
  envFilePath,
  systemdUnit,
  onDismiss,
}: KekOnceCardProps) {
  const [copied, setCopied] = useState(false);
  async function copy() {
    try {
      await navigator.clipboard.writeText(kek);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      /* ignore — operator can hand-select */
    }
  }
  return (
    <div className="rounded border border-amber-900 bg-amber-950/30 p-3 space-y-3">
      <div className="flex items-start gap-2 text-amber-200">
        <AlertTriangle size={14} className="mt-0.5 flex-shrink-0" />
        <div className="text-xs">
          <strong>Copy this KEK now — it will not be shown again.</strong> Paste it into{' '}
          <code>{envVarName}</code> in <code>{envFilePath}</code>, then restart the service
          with <code>sudo systemctl restart {systemdUnit}</code>.
        </div>
      </div>
      <div className="flex items-center gap-2">
        <code className="flex-1 font-mono text-xs break-all bg-ink-900 border border-ink-800 rounded px-2 py-1.5">
          {kek}
        </code>
        <button className="btn-primary" onClick={copy}>
          <Copy size={12} className="mr-1" /> {copied ? 'Copied' : 'Copy'}
        </button>
      </div>
      <button className="btn-ghost text-xs" onClick={onDismiss}>
        I've saved it — hide
      </button>
    </div>
  );
}
